import { useRef, useState } from "react";
import { differenceInMilliseconds } from "date-fns";
import { Props } from "./Shape";
import { useRouter } from "next/navigation";

export default function useOnClick(props: Props) {
  assertOnlyOneClickHandlerProp(props);

  const {
    onClick,
    onLoadableClick,
    fullScreenOverlay,
    routerType = "push",
  } = props;
  const [fullScreenOverlayOpen, setFullScreenOverlayOpen] = useState(false);
  const router = useRouter();
  const initialClickTimeMarker = useRef<Date | null>(null);
  const [loadingState, setLoading] = useState<LoadingState>(false);

  function onLoadComplete(url: string) {
    setLoading("ready");
    setTimeout(() => {
      if (routerType === "push") {
        router.push(url);
      } else {
        router.replace(url);
      }

      initialClickTimeMarker.current = null;
    }, delayToSeeReadyAnimation);
  }

  async function loadUrlThenRedirectToIt() {
    if (!onLoadableClick) {
      return;
    }

    if (initialClickTimeMarker.current) {
      return;
    }

    initialClickTimeMarker.current = new Date();

    setLoading("loading");
    const url = await onLoadableClick();
    await router.prefetch(url);

    const elapsedMs = differenceInMilliseconds(
      new Date(),
      initialClickTimeMarker.current
    );

    // If the loading animation is too fast, we want to make sure it doesn't just flash on and off
    if (elapsedMs < minLoaderLength) {
      setTimeout(() => onLoadComplete(url), minLoaderLength - elapsedMs);
    } else {
      onLoadComplete(url);
    }
  }

  return {
    loadingState,

    async onClick() {
      if (onLoadableClick) {
        loadUrlThenRedirectToIt();

        return;
      }

      onClick?.();

      if (fullScreenOverlay) {
        setFullScreenOverlayOpen;

        return;
      }
    },

    fullScreenOverlayOpen,
    setFullScreenOverlayOpen,
  };
}

export type LoadingState = false | "loading" | "ready";

const minLoaderLength = 1000;
const delayToSeeReadyAnimation = 2500;

function assertOnlyOneClickHandlerProp({ onClick, onLoadableClick }: Props) {
  if (process.env.NODE_ENV === "development") {
    if (onClick && onLoadableClick) {
      throw new Error(
        "Shape: onClick and onRequestLoadThenGo are mutually exclusive"
      );
    }
  }
}
